import type { ExpressionSpecification } from 'maplibre-gl'
import { calculatorMetricOrder } from '../calculatorDatasets.const'
import { useCalculatorDatasets } from '../useCalculatorDatasets'
import { useCalculatorQuery } from '../useCalculatorQuery'
import { calculateMetricSummaryForAreas } from './calculateMetricSummaries'

/** Told apart on the map and on the tinted panel; in this order for the largest values. */
const stylePalette = [
  '#2563eb',
  '#ea580c',
  '#16a34a',
  '#dc2626',
  '#ca8a04',
  '#0891b2',
  '#db2777',
  '#4d7c0f',
]

/** Values beyond the palette, and features with a value that is not in the areas. */
export const calculatorStyleOtherColor = '#6b7280'

export type CalculatorStyleColors = {
  /** The tag the features are colored by (`sum.style`). */
  key: string
  /** Color per value as filters store it (`''` for a missing value). */
  colors: Record<string, string>
}

/** One color per value of the styled tag, the largest values first. */
export const buildCalculatorStyleColors = (key: string, filterValues: string[]) =>
  ({
    key,
    colors: Object.fromEntries(
      filterValues.map((value, index) => [value, stylePalette[index] ?? calculatorStyleOtherColor]),
    ),
  }) satisfies CalculatorStyleColors

/** The color of the features; compares like the server groups (missing or empty is `''`). */
export const calculatorStyleColorExpression = ({ key, colors }: CalculatorStyleColors) => {
  const pairs = Object.entries(colors).flat()
  if (pairs.length === 0) return undefined
  return [
    'match',
    ['to-string', ['coalesce', ['get', key], '']],
    ...pairs,
    calculatorStyleOtherColor,
  ] as ExpressionSpecification
}

/**
 * The colors of the active style, shared by the map (features) and the panel (dots in the
 * breakdown, which is the legend). `undefined` without a style or before an area is summed.
 *
 * The values are ranked like the rows of the panel: by the first metric of the dataset over all
 * areas, with the filter applied to the other tags.
 */
export const useCalculatorStyleColors = () => {
  const { activeDataset, filter, style } = useCalculatorDatasets()
  const rows = useCalculatorQuery().data?.rows
  if (!activeDataset || !style || !rows?.length) return undefined

  const metrics = calculatorMetricOrder.filter((metric) => metric in activeDataset.sumKeys)
  if (metrics.length === 0) return undefined

  const { combined } = calculateMetricSummaryForAreas({
    rows,
    areaIds: [],
    metrics,
    groupByKeys: [style],
    filter,
  })
  const values = combined.groups[0]?.values.map((value) => value.filterValue) ?? []
  return buildCalculatorStyleColors(style, values)
}
